import { useEffect, useRef, useState } from "react";
import { Share2, Mail, Link2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 92z — Multi-channel share dropdown for public event pages.
 * Renders a "Partager" button that opens a popover with WhatsApp, LinkedIn,
 * X/Twitter, Email and Copy-link actions. All labels come from the parent
 * so translation stays in the caller.
 */
export function ShareEventDropdown({ label, channels, copyText, copyLabel, copiedLabel }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      toast.success(copiedLabel);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Copy failed"); }
  };

  const items = [
    { key: "whatsapp", href: channels.whatsapp, label: "WhatsApp", icon: (<svg viewBox="0 0 24 24" className="h-4 w-4" fill="#25D366"><path d="M17.5 14.3c-.3-.1-1.6-.8-1.9-.9-.3-.1-.5-.1-.6.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.4-1.4-.9-.8-1.5-1.7-1.6-2-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2.1-.2-.5-.5-.4-.6-.4h-.5c-.2 0-.5.1-.7.4-.3.3-1 1-1 2.5s1 2.9 1.2 3.1c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.6-.7 1.9-1.3.2-.6.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3zM12 3C7 3 3 7 3 12c0 1.6.4 3.1 1.2 4.4L3 21l4.7-1.2c1.3.7 2.8 1.1 4.3 1.1 5 0 9-4 9-9s-4-9-9-9zm0 16.3c-1.4 0-2.7-.4-3.9-1l-.3-.2-2.8.7.8-2.7-.2-.3c-.7-1.2-1.1-2.5-1.1-3.9 0-4.1 3.3-7.5 7.5-7.5s7.5 3.4 7.5 7.5-3.4 7.4-7.5 7.4z"/></svg>) },
    { key: "linkedin", href: channels.linkedin, label: "LinkedIn", icon: (<svg viewBox="0 0 24 24" className="h-4 w-4" fill="#0A66C2"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3v9zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>) },
    { key: "twitter", href: channels.twitter, label: "X / Twitter", icon: (<svg viewBox="0 0 24 24" className="h-4 w-4" fill="#0f1419"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>) },
    { key: "email", href: channels.email, label: "Email", icon: <Mail className="h-4 w-4 text-neutral-600" /> },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100"
        data-testid="event-share-dropdown-btn"
        title={label}
      >
        <Share2 className="h-4 w-4" />
        {label}
      </button>
      {open && (
        <div className="absolute z-40 mt-2 w-56 origin-top-left rounded-xl border border-neutral-200 bg-white p-1.5 shadow-lg" data-testid="event-share-dropdown-menu">
          {items.map((item) => (
            <a
              key={item.key}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-emerald-50 hover:text-emerald-800"
              data-testid={`event-share-${item.key}`}
            >
              {item.icon}
              {item.label}
            </a>
          ))}
          <button
            type="button"
            onClick={copy}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-emerald-50 hover:text-emerald-800"
            data-testid="event-share-copy"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Link2 className="h-4 w-4 text-neutral-600" />}
            {copied ? copiedLabel : copyLabel}
          </button>
        </div>
      )}
    </div>
  );
}
